import { useEffect, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { Box, Button, Card, Drawer, Stack, Text } from '@mantine/core';
import {
	getSecuritiesOptionsForSelect,
	TRANSACTION_TYPE_OPTIONS,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils';
import {
	CREATE_TRANSACTION_FORM_FIELD,
	useCreateNewTransactionDrawerController,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/CreateNewTransactionDrawer/useCreateNewTransactionDrawerController';
import { MantineDateInput } from '@dealstack/frontend/shared/components/mantineForm/MantineDateInput';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { NumberInput, SegmentedControl, Select } from 'react-hook-form-mantine';
import { DataSecurityTct, TransactionType } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { shouldShowHoldsFor } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/useTransactionFieldsVisibility';
import { isManagementLoanSecurity } from '@dealstack/frontend/shared/cap';
import { InseparableRedemptionModal } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/InseparableRedemptionModal/InseparableRedemptionModal.tsx';
import { RedemptionCounterpartPanel } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/RedemptionCounterpartPanel/RedemptionCounterpartPanel.tsx';
import { StyledForm } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/styled.tsx';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';

interface Props {
	opened: boolean;
	onClose: (createdTransactionId?: string) => void;
	securities?: DataSecurityTct[];
	refreshTransactionsList: () => void;
}

export const CreateNewTransactionDrawer = ({
	opened,
	onClose,
	securities,
	refreshTransactionsList,
}: Props) => {
	const tCapManage = useTranslationCapManage();

	const {
		createTransactionsRequest,
		createPairedIssueRequest,
		createTransactionForm,
		onSubmit,
		personsSelectOptions,
		sellerMipSelectOptions,
		refetchPersons,
		mipSelectOptions,
		unitsDecimalPoints,
		selectedSecurity,
		transactionType,
		hideUnits,
		hideSeller,
		hideBuyer,
		hideBuyerMip,
		hideSellerMip,
		hideBoughtFor,
		hideSoldFor,
		soldForOptions,
		boughtForOptions,
		costLabel,
		hideCost,
		showNomineeFields,
		legalSameAsBeneficial,
		setLegalSameAsBeneficial,
		showTransferInterest,
		transferInterestOptions,
		showRedemptionInterest,
		redemptionCounterpart,
		redemptionInterest,
		pendingRedemption,
		confirmInseparableRedemption,
		cancelInseparableRedemption,
		pendingTransfer,
		confirmTransferCounterpart,
		cancelTransferCounterpart,
		isTransferring,
		isRedeeming,
	} = useCreateNewTransactionDrawerController(refreshTransactionsList, onClose, securities);

	const isManagementLoan = isManagementLoanSecurity(selectedSecurity);

	const securityNameSelectOptions = useMemo(
		() => getSecuritiesOptionsForSelect(securities),
		[securities]
	);

	useEffect(() => {
		if (opened) {
			void refetchPersons();
		}
	}, [opened]);

	useEffect(() => {
		if (sellerMipSelectOptions.length === 1) {
			createTransactionForm.setValue(
				CREATE_TRANSACTION_FORM_FIELD.SELLER_MIP,
				sellerMipSelectOptions[0].value
			);
		}
	}, [sellerMipSelectOptions, createTransactionForm]);

	useEffect(() => {
		if (selectedSecurity) {
			createTransactionForm.setValue(
				CREATE_TRANSACTION_FORM_FIELD.ENTITY_ID,
				selectedSecurity.issuerId
			);
		}
	}, [selectedSecurity, createTransactionForm]);

	const securityLabel =
		transactionType === TransactionType.CONVERSION_SHARE_CAPITAL
			? tCapManage('transactions.drawer.from.label')
			: tCapManage('transactions.drawer.security_name.label');

	const buyerLabel = useMemo(() => {
		if (transactionType === TransactionType.SHAREHOLDER_CONTRIBUTION) {
			return tCapManage('transactions.drawer.contributor.label');
		}
		if (showNomineeFields) {
			return tCapManage('transactions.drawer.buyer_beneficial.label', {
				defaultValue: 'Buyer (Beneficial interest)',
			});
		}
		return tCapManage('transactions.drawer.buyer.label');
	}, [transactionType, tCapManage, showNomineeFields]);

	const buyerPlaceholder = useMemo(() => {
		if (transactionType === TransactionType.SHAREHOLDER_CONTRIBUTION) {
			return tCapManage('transactions.drawer.contributor.placeholder');
		}
		return tCapManage('transactions.drawer.buyer.placeholder');
	}, [transactionType, tCapManage]);

	const sellerLabel = useMemo(() => {
		if (transactionType === TransactionType.REDUCTION_IN_SHARE_CAPITAL) {
			return tCapManage('transactions.drawer.recipient_capital.label');
		}
		if (transactionType === TransactionType.DIVIDEND) {
			return tCapManage('transactions.drawer.recipient_dividend.label');
		}
		return tCapManage('transactions.drawer.seller.label');
	}, [transactionType, tCapManage]);

	const sellerPlaceholder = useMemo(() => {
		if (transactionType === TransactionType.REDUCTION_IN_SHARE_CAPITAL) {
			return tCapManage('transactions.drawer.recipient_capital.placeholder');
		}
		if (transactionType === TransactionType.DIVIDEND) {
			return tCapManage('transactions.drawer.recipient_dividend.placeholder');
		}
		return tCapManage('transactions.drawer.seller.placeholder');
	}, [transactionType, tCapManage]);

	// The nominee split moves the bought-for inside the bordered card below, where it sits directly
	// under the buyer it qualifies and names the rail it belongs to. It applies to the BENEFICIAL
	// holder only — the legal leg is issued without one.
	const beneficialBoughtForSelect = (
		<Select
			name={CREATE_TRANSACTION_FORM_FIELD.BOUGHT_FOR}
			control={createTransactionForm.control}
			data={boughtForOptions}
			label={tCapManage('transactions.drawer.bought_for.label_for_interest', {
				interest: tCapManage('transactions.interest.beneficial'),
			})}
			placeholder={tCapManage('transactions.drawer.bought_for.placeholder')}
			size="md"
			searchable
			clearable
			mt="xs"
			data-testid={TEST_ELEMENT_IDS.CAP_TRANSACTION_CREATE_BOUGHT_FOR_SELECT}
		/>
	);

	const buyerSelect = !hideBuyer && (
		<Select
			name={CREATE_TRANSACTION_FORM_FIELD.BUYER}
			control={createTransactionForm.control}
			data-testid={TEST_ELEMENT_IDS.CAP_TRANSACTION_CREATE_BUYER_SELECT}
			data={personsSelectOptions}
			label={buyerLabel}
			placeholder={buyerPlaceholder}
			size="md"
			searchable
			clearable={false}
		/>
	);

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			trapFocus={false}
			title={tCapManage('transactions.drawer.add_title')}
		>
			{(createTransactionsRequest.isPending ||
				createPairedIssueRequest.isPending ||
				isRedeeming) && <InlineLoader />}

			<FormProvider {...createTransactionForm}>
				<StyledForm onSubmit={createTransactionForm.handleSubmit(onSubmit(true))}>
					<MantineDateInput
						name={CREATE_TRANSACTION_FORM_FIELD.DATE}
						label={tCapManage('transactions.drawer.date.label')}
						data-testid={TEST_ELEMENT_IDS.CAP_TRANSACTION_CREATE_DATE_INPUT}
						maxDate={new Date()}
						size="md"
					/>
					<Select
						name={CREATE_TRANSACTION_FORM_FIELD.SECURITY_ID}
						data-testid={TEST_ELEMENT_IDS.CAP_TRANSACTION_CREATE_SECURITY_SELECT}
						control={createTransactionForm.control}
						data={securityNameSelectOptions}
						label={securityLabel}
						placeholder={tCapManage('transactions.drawer.security_name.placeholder')}
						size="md"
						searchable
					/>
					<Select
						name={CREATE_TRANSACTION_FORM_FIELD.TYPE}
						control={createTransactionForm.control}
						data-testid={TEST_ELEMENT_IDS.CAP_TRANSACTION_CREATE_TYPE_SELECT}
						data={TRANSACTION_TYPE_OPTIONS}
						label={tCapManage('transactions.drawer.transaction_type.label')}
						placeholder={tCapManage('transactions.drawer.transaction_type.placeholder')}
						size="md"
						clearable={false}
					/>
					{transactionType === TransactionType.CONVERSION_SHARE_CAPITAL && (
						<Select
							name={CREATE_TRANSACTION_FORM_FIELD.CONVERSION_TARGET_SECURITY_ID}
							data-testid={
								TEST_ELEMENT_IDS.CAP_TRANSACTION_CREATE_CONVERSION_TARGET_SECURITY_SELECT
							}
							control={createTransactionForm.control}
							data={securityNameSelectOptions}
							label={tCapManage('transactions.drawer.to.label')}
							placeholder={tCapManage(
								'transactions.drawer.conversion_target_security.placeholder'
							)}
							size="md"
							searchable
						/>
					)}
					{!showNomineeFields && buyerSelect}
					{showNomineeFields && (
						/* The design draws the two halves of the buyer — who benefits and who holds
						   legal title — inside one bordered card, so the split reads as a single
						   decision rather than two unrelated fields further apart in the form. */
						<Card withBorder padding="xs">
							<Stack gap="md">
								<Box>
									{buyerSelect}
									{shouldShowHoldsFor({
										hideHoldsFor: hideBoughtFor,
										partyIsSecurityIssuer: hideBuyer,
										isManagementLoan,
									}) && beneficialBoughtForSelect}
								</Box>
								<Box>
									{/* space-between on the switch itself, not a wrapper: the switch
									    renders its own label, so a Group around it has one child and
									    nothing to distribute — which left the toggle mid-row. */}
									<AdvancedSwitch
										selectedValue={legalSameAsBeneficial}
										onChange={(event) => setLegalSameAsBeneficial(!!event)}
										justify="space-between"
										// component="span": AdvancedSwitch already wraps leftText in a Text,
										// which renders a <p>, so a nested Text nests <p> inside <p>.
										leftText={
											<Text size="sm" component="span">
												{tCapManage(
													'transactions.drawer.buyer_legal.same_as_beneficial'
												)}
											</Text>
										}
										unselectedLetterSpacing="0"
										data-testid={
											TEST_ELEMENT_IDS.CAP_TRANSACTION_CREATE_NOMINEE_SAME_TOGGLE
										}
									/>
									{/* Stays mounted and greys out while the legal holder tracks the
									    beneficial buyer, so the field the toggle governs is always
									    visible and the drawer keeps a stable height. */}
									<Select
										name={CREATE_TRANSACTION_FORM_FIELD.NOMINEE_ID}
										control={createTransactionForm.control}
										data={personsSelectOptions}
										withAsterisk={!legalSameAsBeneficial}
										disabled={legalSameAsBeneficial}
										label={tCapManage('transactions.drawer.buyer_legal.label')}
										placeholder={tCapManage(
											'transactions.drawer.nominee.placeholder'
										)}
										size="md"
										searchable
										mt="xs"
										data-testid={
											TEST_ELEMENT_IDS.CAP_TRANSACTION_CREATE_NOMINEE_SELECT
										}
									/>
								</Box>
							</Stack>
						</Card>
					)}
					{showTransferInterest && (
						/* Three mutually exclusive rails read better side by side than behind a
						   dropdown, and the choice drives the rest of the form. The label is
						   carried by aria-label rather than shown, so the control reads the same
						   to a screen reader without a visible caption above it. */
						<SegmentedControl
							name={CREATE_TRANSACTION_FORM_FIELD.TITLE}
							control={createTransactionForm.control}
							data={transferInterestOptions}
							data-testid={
								TEST_ELEMENT_IDS.CAP_TRANSACTION_CREATE_TRANSFER_INTEREST_SELECT
							}
							aria-label={tCapManage('transactions.drawer.transfer_interest.label')}
						/>
					)}
					{showRedemptionInterest && (
						/* CAP-2033: the interest being redeemed is a choice, not a fixed indicator.
						   Retiring one interest retires its counterpart title over the same serials,
						   so the consequence copy below promises correspondence rather than warning
						   that the other rail is left behind. */
						/* Stack, not Box: SegmentedControl is inline-flex, so a block wrapper lets it
						   shrink to its own content and the rail renders as a stray half-width button
						   group beside the full-width fields. The transfer control sits directly in
						   the form's flex column and is stretched by it; a stretching wrapper gives
						   the same result while keeping the caption attached to the control. */
						<Stack gap={4}>
							<SegmentedControl
								name={CREATE_TRANSACTION_FORM_FIELD.TITLE}
								control={createTransactionForm.control}
								data={transferInterestOptions}
								data-testid={
									TEST_ELEMENT_IDS.CAP_TRANSACTION_CREATE_REDEMPTION_INTEREST_SELECT
								}
								aria-label={tCapManage(
									'transactions.drawer.redemption_interest.label'
								)}
							/>
							<Text size="sm" c="dimmed">
								{tCapManage(
									'transactions.drawer.redemption_interest.correspondence'
								)}
							</Text>
							{/* CAP-2039: the copy above promises a correspondence; this names the
							    other side of it, while the form is still being filled in. Read-only —
							    the counterpart follows from legal structure, and the resolver is the
							    only thing that knows where that title currently sits. */}
							<RedemptionCounterpartPanel
								preview={redemptionCounterpart}
								interest={redemptionInterest}
								securityName={selectedSecurity?.name ?? ''}
							/>
						</Stack>
					)}
					{/* Non-nominee transactions keep the plain, un-suffixed bought-for: there is only one
					    interest, so qualifying it by rail would be noise. */}
					{!showNomineeFields &&
						shouldShowHoldsFor({
							hideHoldsFor: hideBoughtFor,
							partyIsSecurityIssuer: hideBuyer,
							isManagementLoan,
						}) && (
							<Select
								name={CREATE_TRANSACTION_FORM_FIELD.BOUGHT_FOR}
								control={createTransactionForm.control}
								data={boughtForOptions}
								label={tCapManage('transactions.drawer.bought_for.label')}
								placeholder={tCapManage(
									'transactions.drawer.bought_for.placeholder'
								)}
								size="md"
								searchable
								clearable={true}
								data-testid={
									TEST_ELEMENT_IDS.CAP_TRANSACTION_CREATE_BOUGHT_FOR_SELECT
								}
							/>
						)}
					{!hideSeller && (
						<Select
							name={CREATE_TRANSACTION_FORM_FIELD.SELLER}
							data-testid={TEST_ELEMENT_IDS.CAP_TRANSACTION_CREATE_SELLER_SELECT}
							control={createTransactionForm.control}
							label={sellerLabel}
							placeholder={sellerPlaceholder}
							data={personsSelectOptions}
							size="md"
							searchable
							clearable={false}
						/>
					)}
					{shouldShowHoldsFor({
						hideHoldsFor: hideSoldFor,
						partyIsSecurityIssuer: hideSeller,
						isManagementLoan,
					}) && (
						<Select
							name={CREATE_TRANSACTION_FORM_FIELD.SOLD_FOR}
							control={createTransactionForm.control}
							label={tCapManage('transactions.drawer.sold_for.label')}
							placeholder={tCapManage('transactions.drawer.sold_for.placeholder')}
							data={soldForOptions}
							size="md"
							searchable
							clearable={true}
						/>
					)}
					{!hideBuyerMip && (
						<Select
							name={CREATE_TRANSACTION_FORM_FIELD.BUYER_MIP}
							control={createTransactionForm.control}
							label={
								showNomineeFields
									? tCapManage('transactions.drawer.buyer_mip.label_both')
									: tCapManage('transactions.drawer.buyer_mip.label')
							}
							placeholder={tCapManage('transactions.drawer.buyer_mip.label')}
							data={mipSelectOptions}
							size="md"
							searchable
							clearable={false}
							data-testid={TEST_ELEMENT_IDS.CAP_TRANSACTION_CREATE_BUYER_HOLDING}
						/>
					)}

					{!hideSellerMip && sellerMipSelectOptions.length > 1 && (
						<Select
							name={CREATE_TRANSACTION_FORM_FIELD.SELLER_MIP}
							control={createTransactionForm.control}
							label={tCapManage('transactions.drawer.seller_mip.label')}
							placeholder={tCapManage('transactions.drawer.seller_mip.placeholder')}
							data={sellerMipSelectOptions}
							size="md"
							searchable
							clearable={false}
							readOnly={sellerMipSelectOptions.length <= 1}
							disabled={sellerMipSelectOptions.length <= 1}
						/>
					)}

					{!hideUnits && (
						<NumberInput
							name={CREATE_TRANSACTION_FORM_FIELD.NUMBER}
							data-testid={TEST_ELEMENT_IDS.CAP_TRANSACTION_CREATE_NUMBER_INPUT}
							control={createTransactionForm.control}
							label={tCapManage('transactions.drawer.number_of_units.label')}
							placeholder={tCapManage(
								'transactions.drawer.number_of_units.placeholder'
							)}
							size="md"
							decimalScale={unitsDecimalPoints}
						/>
					)}
					{!hideCost && (
						<NumberInput
							name={CREATE_TRANSACTION_FORM_FIELD.PRICE}
							data-testid={TEST_ELEMENT_IDS.CAP_TRANSACTION_CREATE_PRICE_INPUT}
							control={createTransactionForm.control}
							label={costLabel || tCapManage('transactions.drawer.price.label')}
							placeholder={tCapManage('transactions.drawer.price.placeholder')}
							size="md"
							step={0.01}
							decimalScale={2}
						/>
					)}

					<Button
						type="submit"
						fullWidth
						mt="auto"
						data-testid={TEST_ELEMENT_IDS.CAP_TRANSACTION_CREATE_SUBMIT}
					>
						{tCapManage('transactions.drawer.create_button')}
					</Button>

					<Button
						onClick={createTransactionForm.handleSubmit(onSubmit(false))}
						data-testid={TEST_ELEMENT_IDS.CAP_TRANSACTION_CREATE_SUBMIT_ADD_ANOTHER}
						fullWidth
						variant="default"
					>
						{tCapManage('transactions.drawer.create_add_another_button')}
					</Button>
				</StyledForm>
			</FormProvider>

			{pendingRedemption ? (
				<InseparableRedemptionModal
					disclosure={pendingRedemption.disclosure}
					isSubmitting={isRedeeming}
					onProceed={() => void confirmInseparableRedemption()}
					onCancel={cancelInseparableRedemption}
				/>
			) : null}

			{pendingTransfer ? (
				<InseparableRedemptionModal
					disclosure={pendingTransfer.disclosure}
					isSubmitting={isTransferring}
					proceedLabel={tCapManage('transactions.transfer_counterpart.proceed')}
					onProceed={confirmTransferCounterpart}
					onCancel={cancelTransferCounterpart}
				/>
			) : null}
		</Drawer>
	);
};
